/**
 * Messaging realtime client — wires three SSE streams (inbox, presence,
 * current thread) into HTMX refresh triggers and lightweight DOM mutations.
 *
 * Design notes:
 *   - Uses native EventSource so we carry no extra runtime dependency.
 *   - Polling remains as a graceful fallback (see messageBadge.html). If
 *     SSE is blocked by a proxy, the UI still refreshes every 60s.
 *   - Reconnects use exponential-ish jitter (base 5s + random 0-5s) so a
 *     server restart does not produce a thundering-herd of simultaneous
 *     reconnects.
 *   - After 25 minutes we close+reopen the connection on purpose — long
 *     before the 30m access-token TTL — so the refresh cookie can refresh
 *     the JWT through a regular HTTP request before the next open.
 *   - Does nothing when {@code EventSource} is unavailable, or when the
 *     user navigates to a page that has no badge and no thread container
 *     (e.g. public pages); the first DOM probe on load is a cheap
 *     no-op in that case.
 */
(function () {
    'use strict';

    if (typeof window === 'undefined') return;
    if (typeof window.EventSource === 'undefined') return;

    var INBOX_URL = '/api/messages/stream/inbox';
    var PRESENCE_URL = '/api/messages/stream/presence';
    var THREAD_URL_BASE = '/api/messages/stream/thread/';
    var RECONNECT_BASE_MS = 5000;
    var RECONNECT_JITTER_MS = 5000;
    var SOFT_RECONNECT_MS = 25 * 60 * 1000;
    // Give up after this many consecutive failed opens with no successful
    // `onopen` in between. Protects against a logged-out tab hammering the
    // server with 401s forever (browsers don't expose SSE status codes, so
    // we can't tell 401 from network errors directly).
    var MAX_CONSECUTIVE_FAILURES = 5;

    function hasElement(selector) {
        try {
            return !!document.querySelector(selector);
        } catch (_) {
            return false;
        }
    }

    function triggerHtmx(selector, event) {
        try {
            var el = document.querySelector(selector);
            if (el && window.htmx) {
                window.htmx.trigger(el, event);
            }
        } catch (_) { /* ignore */ }
    }

    function parseJson(data) {
        if (!data || typeof data !== 'string') return null;
        if (data.charAt(0) === ':') return null; // SSE comment
        try {
            return JSON.parse(data);
        } catch (_) {
            return null;
        }
    }

    /**
     * Per-stream connection with jitter reconnect and scheduled soft reconnect.
     * {@code onPayload(evtData)} is invoked for every non-heartbeat event.
     */
    function makeStream(name, url, onPayload) {
        var es = null;
        var reconnectTimer = null;
        var softReconnectTimer = null;
        var closed = false;
        var consecutiveFailures = 0;

        function cleanup() {
            if (es) {
                try { es.close(); } catch (_) { /* ignore */ }
                es = null;
            }
            if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
            if (softReconnectTimer) { clearTimeout(softReconnectTimer); softReconnectTimer = null; }
        }

        function scheduleReconnect() {
            if (closed) return;
            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                // Most likely unauthenticated or otherwise permanently denied.
                // Stop retrying; a page reload after sign-in will re-open us.
                cleanup();
                return;
            }
            var delay = RECONNECT_BASE_MS + Math.floor(Math.random() * RECONNECT_JITTER_MS);
            reconnectTimer = setTimeout(open, delay);
        }

        function open() {
            if (closed) return;
            cleanup();
            try {
                es = new EventSource(url, { withCredentials: true });
            } catch (_) {
                consecutiveFailures += 1;
                scheduleReconnect();
                return;
            }
            es.onopen = function () {
                consecutiveFailures = 0;
            };
            es.onmessage = function (ev) {
                var payload = parseJson(ev && ev.data);
                if (!payload) return;
                try { onPayload(payload); } catch (_) { /* ignore handler errors */ }
            };
            es.onerror = function () {
                // EventSource schedules its own retry too, but we want the
                // jitter window, so close and reopen ourselves.
                consecutiveFailures += 1;
                scheduleReconnect();
            };
            softReconnectTimer = setTimeout(function () { open(); }, SOFT_RECONNECT_MS);
        }

        function close() {
            closed = true;
            cleanup();
        }

        return { open: open, close: close, name: name };
    }

    function onInbox(payload) {
        if (!payload || payload.type !== 'unread_changed') return;
        triggerHtmx('#message-badge', 'realtime:refresh');
    }

    function lastRenderedMessageId(container) {
        var bubbles = container.querySelectorAll('[data-message-id]');
        if (!bubbles.length) return null;
        return bubbles[bubbles.length - 1].getAttribute('data-message-id');
    }

    /**
     * Drop bubbles already in the timeline so a racing 204-refresh and
     * SSE append cannot paint the same message twice. Orphan groups with
     * no remaining bubble are discarded; if nothing new is left, return
     * null so the caller skips insert and scroll.
     */
    function dedupeTimelineHtml(container, html) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var nodes = tpl.content.querySelectorAll('[data-message-id]');
        for (var i = 0; i < nodes.length; i++) {
            var id = nodes[i].getAttribute('data-message-id');
            if (!id) continue;
            if (container.querySelector('[data-message-id="' + id + '"]')) {
                nodes[i].remove();
            }
        }
        var groups = tpl.content.querySelectorAll('.msg-group');
        for (var g = 0; g < groups.length; g++) {
            if (!groups[g].querySelector('.msg-bubble') && !groups[g].getAttribute('data-message-id')) {
                groups[g].remove();
            }
        }
        if (!tpl.content.querySelector('[data-message-id], .msg-system')) {
            return null;
        }
        return tpl.content;
    }

    function fetchTimelineSince(container, threadId, sinceId) {
        if (container.dataset.timelineFetching === '1') {
            container.dataset.timelineQueued = '1';
            return;
        }
        container.dataset.timelineFetching = '1';
        var role = (container.getAttribute('data-viewer-role') || '').toLowerCase();
        var url = '/messages/' + encodeURIComponent(threadId)
                + '/fragments/timeline-since?role=' + encodeURIComponent(role);
        if (sinceId) url += '&sinceMessageId=' + encodeURIComponent(sinceId);
        fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
            .then(function (r) {
                if (r.status === 204 || !r.ok) return '';
                return r.text();
            })
            .then(function (html) {
                var pending = container.querySelectorAll('.msg-group--pending');
                for (var p = 0; p < pending.length; p++) pending[p].remove();
                if (!html) return;
                var fragment = dedupeTimelineHtml(container, html);
                if (!fragment) return;
                var near = container.scrollTop + container.clientHeight >= container.scrollHeight - 40;
                var entering = fragment.querySelectorAll('.msg-group');
                for (var e = 0; e < entering.length; e++) entering[e].classList.add('msg-group--enter');
                container.appendChild(fragment);
                if (near) {
                    container.scrollTop = container.scrollHeight;
                    hideJumpPill(container);
                } else {
                    showJumpPill(container);
                }
            })
            .catch(function () { /* ignore network failures; next event will retry */ })
            .then(function () {
                container.dataset.timelineFetching = '0';
                if (container.dataset.timelineQueued === '1') {
                    container.dataset.timelineQueued = '0';
                    fetchTimelineSince(container, threadId, lastRenderedMessageId(container));
                }
            });
    }

    function jumpPill(container) {
        var pane = container.closest('.messaging-pane');
        if (!pane) return null;
        var btn = pane.querySelector('.messaging-timeline__jump');
        if (btn) return btn;
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'messaging-timeline__jump';
        btn.hidden = true;
        btn.setAttribute('aria-hidden', 'true');
        btn.textContent = 'New messages ↓';
        btn.addEventListener('click', function () {
            container.scrollTop = container.scrollHeight;
            hideJumpPill(container);
        });
        pane.appendChild(btn);
        if (!container.dataset.jumpScrollBound) {
            container.dataset.jumpScrollBound = '1';
            container.addEventListener('scroll', function () {
                if (container.scrollTop + container.clientHeight >= container.scrollHeight - 40) {
                    hideJumpPill(container);
                }
            }, { passive: true });
        }
        return btn;
    }

    function showJumpPill(container) {
        var btn = jumpPill(container);
        if (!btn) return;
        btn.hidden = false;
        btn.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(function () {
            btn.classList.add('is-visible');
        });
    }

    function hideJumpPill(container) {
        var pane = container.closest('.messaging-pane');
        if (!pane) return;
        var btn = pane.querySelector('.messaging-timeline__jump');
        if (!btn) return;
        btn.classList.remove('is-visible');
        btn.setAttribute('aria-hidden', 'true');
        window.setTimeout(function () {
            if (!btn.classList.contains('is-visible')) btn.hidden = true;
        }, 220);
    }

    function onThreadEvent(payload) {
        if (!payload || !payload.type) return;
        var container = document.querySelector('[data-messaging-timeline]');
        if (!container) return;
        var threadId = container.getAttribute('data-thread-id');
        if (!threadId || payload.threadId !== threadId) return;
        if (payload.type === 'typing') {
            var own = (container.getAttribute('data-viewer-role') || '').toUpperCase();
            if ((payload.senderRole || '').toUpperCase() === own) return;
            showTyping(container);
            return;
        }
        if (payload.type !== 'message_appended') return;
        hideTyping(container);
        if ((payload.senderRole || '').toUpperCase() === 'JOB_SEEKER') {
            try {
                window.dispatchEvent(new CustomEvent('messaging:hide-quick-replies'));
            } catch (_) { /* ignore */ }
        }
        var sinceId = lastRenderedMessageId(container);
        fetchTimelineSince(container, threadId, sinceId);
    }

    var typingTimer = null;
    function showTyping(container) {
        var row = container.querySelector('.msg-typing');
        if (!row) {
            row = document.createElement('div');
            row.className = 'msg-typing';
            row.setAttribute('aria-live', 'polite');
            var label = document.createElement('span');
            label.className = 'msg-typing__label';
            label.textContent = 'Typing';
            var dots = document.createElement('span');
            dots.className = 'msg-typing__dots';
            dots.setAttribute('aria-hidden', 'true');
            dots.appendChild(document.createElement('span'));
            dots.appendChild(document.createElement('span'));
            dots.appendChild(document.createElement('span'));
            row.appendChild(label);
            row.appendChild(dots);
            container.appendChild(row);
        }
        container.scrollTop = container.scrollHeight;
        if (typingTimer) clearTimeout(typingTimer);
        typingTimer = setTimeout(function () { hideTyping(container); }, 5000);
    }

    function hideTyping(container) {
        if (typingTimer) { clearTimeout(typingTimer); typingTimer = null; }
        var row = container.querySelector('.msg-typing');
        if (row) row.remove();
    }

    function updatePresenceDom(selector, online, lastSeenAt) {
        if (!selector) return;
        var nodes = document.querySelectorAll(selector);
        for (var i = 0; i < nodes.length; i++) {
            var el = nodes[i];
            if (el.classList.contains('msg-avatar')) {
                if (online) el.classList.add('msg-avatar--online');
                else el.classList.remove('msg-avatar--online');
            }
            if (el.classList.contains('msg-presence')) {
                if (online) {
                    el.classList.add('msg-presence--online');
                    el.classList.remove('msg-presence--offline');
                    el.textContent = 'Online now';
                } else {
                    el.classList.remove('msg-presence--online');
                    el.classList.add('msg-presence--offline');
                    if (lastSeenAt) el.textContent = 'Last seen recently';
                }
            }
        }
    }

    function onPresence(payload) {
        if (!payload || payload.type !== 'presence') return;
        // Org-level events carry organizationId; per-account events carry
        // accountId. Route by whichever is present so one client subscription
        // can drive both individual dots and whole-team dots.
        var selector = null;
        if (payload.organizationId) {
            selector = '[data-organization-id="' + payload.organizationId + '"]';
        } else if (payload.accountId) {
            selector = '[data-account-id="' + payload.accountId + '"]';
        }
        updatePresenceDom(selector, !!payload.online, payload.lastSeenAt);
    }

    function currentThreadId() {
        var container = document.querySelector('[data-messaging-timeline]');
        if (!container) return null;
        var id = container.getAttribute('data-thread-id');
        return id && /^[0-9a-f-]{10,}$/i.test(id) ? id : null;
    }

    var streams = [];

    function startAll() {
        // Inbox — only when the user has a badge rendered (i.e. is logged in
        // to a portal that shows messages).
        if (hasElement('#message-badge')) {
            var inbox = makeStream('inbox', INBOX_URL, onInbox);
            inbox.open();
            streams.push(inbox);
        }

        // Presence — open whenever *any* avatar or presence label has a
        // targetable hook (per-account or per-organization). Either one is
        // enough for the client to route incoming events.
        if (hasElement('[data-account-id],[data-organization-id]')) {
            var presence = makeStream('presence', PRESENCE_URL, onPresence);
            presence.open();
            streams.push(presence);
        }

        // Thread — only when a conversation is on screen.
        var tid = currentThreadId();
        if (tid) {
            var thread = makeStream('thread', THREAD_URL_BASE + tid, onThreadEvent);
            thread.open();
            streams.push(thread);
        }
    }

    function stopAll() {
        for (var i = 0; i < streams.length; i++) {
            try { streams[i].close(); } catch (_) { /* ignore */ }
        }
        streams = [];
    }

    window.messagingRealtime = {
        restart: function () { stopAll(); startAll(); },
        stop: stopAll,
        refreshTimeline: function () {
            var container = document.querySelector('[data-messaging-timeline]');
            if (!container) return;
            var threadId = container.getAttribute('data-thread-id');
            if (!threadId) return;
            fetchTimelineSince(container, threadId, lastRenderedMessageId(container));
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startAll);
    } else {
        startAll();
    }

    window.addEventListener('beforeunload', stopAll);
})();
